import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, ShieldCheck } from 'lucide-react';
import { TERMS_SECTIONS } from '@/constants/legal';

// The exact zero-tolerance clause from the Terms page (section '4. User
// Conduct') -- quoted from the same constant that page renders, rather
// than re-typed here, so this gate can never drift out of sync with the
// Terms text it's supposed to be presenting.
const CONDUCT_CLAUSE = (TERMS_SECTIONS.find(([title]) => title.includes('User Conduct')) || [])[1] || '';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter 117.60 — Complete-profile gate.
 *
 * Pre-117.60, the Google OAuth path silently created users with NO
 * city/state. Iter 117.60 (a) hardens the backend so new Google
 * sign-ins now 404 instead of stealth-create. This component (b)
 * heals the existing handful of prod users who slipped through:
 * on next authenticated route hit, if `user.city` or `user.state`
 * is empty, show a one-screen capture before letting them continue.
 *
 * Iter 118.42 — App Store Guideline 5.1.1(v): this can no longer be a
 * hard block (only phone/city/state/country being REQUIRED is what
 * Apple flagged; this gate was a second, separate way the same two of
 * those four ended up required). Added a "Maybe later" skip that
 * stamps profileGateSkippedAt so the gate leaves the user alone for 30
 * days instead of reappearing on the very next protected route.
 */
const SKIP_SNOOZE_DAYS = 30;

const CompleteProfileGate = ({ user, onUserUpdate, children }) => {
  const location = useLocation();
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [agreeing, setAgreeing] = useState(false);

  // Allow these routes to render without the gate, so the user can
  // still log out / see legal pages even if they're stuck.
  //
  // /billing/delegate is here for a different reason: it's deliberately
  // designed so a delegate needs NO account at all to pay a community's
  // bill. A delegate who happens to ALSO be a logged-in Gr8ful user with
  // an incomplete profile (Iter 117.60's exact healing scenario) would
  // otherwise be blocked here before ever reaching the page they were
  // sent a link to -- contradicting that design for no reason tied to
  // paying a bill.
  const ALLOWLIST = ['/terms', '/privacy', '/cookie-policy', '/community-standards', '/community-guidelines', '/safety-contact', '/contact', '/sms-opt-in', '/billing/delegate'];
  if (ALLOWLIST.some(p => location.pathname.startsWith(p))) {
    return children;
  }

  // App Review Guideline 1.2 -- terms must be agreed to, and the email/
  // Google-new-user paths already stamp termsAcceptedAt at signup, so its
  // absence here means this account slipped through without ever seeing
  // them (Sign in with Apple's new-account path did exactly this before
  // this gate existed; a pre-existing account from before termsAcceptedAt
  // was introduced would also land here, which is correct -- Apple wants
  // every account to have agreed, not just new ones). Deliberately NOT
  // skippable, unlike the profile gate below: consent isn't optional.
  const needsTerms = user && !user.termsAcceptedAt;
  if (needsTerms) {
    const handleAgreeTerms = async () => {
      setAgreeing(true);
      try {
        const res = await axios.patch(`${API}/users/${user.id}`, {
          termsAcceptedAt: new Date().toISOString(),
        });
        onUserUpdate?.(res.data || { ...user, termsAcceptedAt: new Date().toISOString() });
      } catch (e) {
        toast.error('Could not save. Please try again.');
      } finally {
        setAgreeing(false);
      }
    };
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-[#FDFBF7]"
        data-testid="terms-agreement-gate"
      >
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E6DF] p-6 sm:p-8 shadow-[0_2px_12px_rgba(44,51,51,0.06)]">
          <div className="flex items-center gap-2 mb-2 text-[#4E8B7C]">
            <ShieldCheck size={18} />
            <span className="text-xs font-medium uppercase tracking-wider">One last step</span>
          </div>
          <h1
            className="text-2xl text-[#2C3333] mb-3"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Our community standards
          </h1>
          <p className="text-sm text-[#5C6666] mb-4 leading-relaxed">{CONDUCT_CLAUSE}</p>
          <p className="text-sm text-[#8C9696] mb-6">
            Read the full{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-[#4E8B7C] underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/community-guidelines" target="_blank" rel="noreferrer" className="text-[#4E8B7C] underline">Community Standards</a>.
          </p>
          <Button
            onClick={handleAgreeTerms}
            disabled={agreeing}
            className="w-full bg-[#4E8B7C] text-white hover:bg-[#3D7265] rounded-full h-12"
            data-testid="terms-agreement-accept"
          >
            {agreeing ? 'One moment…' : 'I agree — Continue'}
          </Button>
        </div>
      </div>
    );
  }

  const missingProfile = user && (!user.city || !user.state);
  const skippedRecently = user?.profileGateSkippedAt
    && (Date.now() - new Date(user.profileGateSkippedAt).getTime()) < SKIP_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  const needs = missingProfile && !skippedRecently;
  if (!needs) return children;

  const handleSkip = async () => {
    setSkipping(true);
    const skippedAt = new Date().toISOString();
    try {
      const res = await axios.patch(`${API}/users/${user.id}`, { profileGateSkippedAt: skippedAt });
      onUserUpdate?.(res.data || { ...user, profileGateSkippedAt: skippedAt });
    } catch (e) {
      // Non-fatal by design, matching handleSave's own error handling below
      // -- if this PATCH fails the gate just shows again next time, same
      // as if the user had never tapped "Maybe later" at all.
      toast.error('Could not skip right now. Please try again.');
    } finally {
      setSkipping(false);
    }
  };

  const handleSave = async () => {
    if (!city.trim() || !state.trim()) {
      toast.error('Both city and state are required.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/users/${user.id}`, {
        city: city.trim(),
        state: state.trim(),
      });
      const updated = res.data || { ...user, city: city.trim(), state: state.trim() };
      onUserUpdate?.(updated);
      toast.success('Profile updated. Welcome!');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-[#FDFBF7]"
      data-testid="complete-profile-gate"
    >
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E6DF] p-6 sm:p-8 shadow-[0_2px_12px_rgba(44,51,51,0.06)]">
        <div className="flex items-center gap-2 mb-2 text-[#4E8B7C]">
          <MapPin size={18} />
          <span className="text-xs font-medium uppercase tracking-wider">One last step</span>
        </div>
        <h1
          className="text-2xl text-[#2C3333] mb-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          Finish your profile
        </h1>
        <p className="text-sm text-[#5C6666] mb-6 leading-relaxed">
          We use your city and state to show you nearby prayer groups and
          to surface relevant prayer requests. This isn't shared publicly on
          your prayer cards.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="profile-city" className="text-sm text-[#2C3333]">
              City <span className="text-[#D9534F]">*</span>
            </Label>
            <Input
              id="profile-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Greenville"
              autoFocus
              className="mt-1.5 bg-white border-[#E8E6DF] h-12"
              data-testid="complete-profile-city"
            />
          </div>
          <div>
            <Label htmlFor="profile-state" className="text-sm text-[#2C3333]">
              State / Province <span className="text-[#D9534F]">*</span>
            </Label>
            <Input
              id="profile-state"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="e.g., SC"
              className="mt-1.5 bg-white border-[#E8E6DF] h-12"
              data-testid="complete-profile-state"
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !city.trim() || !state.trim()}
            className="w-full bg-[#4E8B7C] text-white hover:bg-[#3D7265] rounded-full h-12 mt-2"
            data-testid="complete-profile-save"
          >
            {saving ? 'Saving…' : 'Save and continue'}
          </Button>

          <button
            type="button"
            onClick={handleSkip}
            disabled={skipping}
            className="w-full text-center text-sm text-[#8C9696] hover:text-[#5C6666] transition-colors"
            data-testid="complete-profile-skip"
          >
            {skipping ? 'One moment…' : 'Maybe later'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfileGate;
